import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { COST_BOM_DEMO_STRINGS } from '~/widgets/CostBomDemo/strings'
import { COUNTRY_OPTIONS } from '~/widgets/TariffCalculator/utils'

type Group = { group: string; countries: string[] }

/** The tariff calculator's list, kept grouped so the common ones stay on top. */
const GROUPS: Group[] = COUNTRY_OPTIONS.flatMap((item) => {
  if (typeof item !== 'object' || item === null || !('group' in item)) return []
  return [
    {
      group: item.group,
      countries: item.items.map((sub) =>
        typeof sub === 'string' ? sub : sub.value,
      ),
    },
  ]
})

interface HeroSourcingSelectProps {
  value: string
  onChange: (country: string) => void
  /** Off where the surrounding row already labels the control. */
  showLabel?: boolean
}

/**
 * Where the product is made, as a pill that opens a list.
 *
 * Not a native select: one hundred and ninety options in a system menu is a
 * grey slab over the hero, and the control has to sit inside the composer
 * without looking like a form field. The list carries a filter because that is
 * what the native control was doing for us.
 */
export function HeroSourcingSelect({
  value,
  onChange,
  showLabel = true,
}: HeroSourcingSelectProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  /* Closing always clears the filter. A list that reopens still showing the
     last search looks like it has lost most of its countries. */
  const close = useCallback(() => {
    setOpen(false)
    setQuery('')
  }, [])

  useEffect(() => {
    if (!open) return () => {}

    const onPointerDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) close()
    }
    // Captured and marked handled, so an Escape that closes this list is not
    // also taken by a dialog around it.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      close()
    }

    searchRef.current?.focus()
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown, true)
    }
  }, [open, close])

  /**
   * The groups organise a list nobody is reading through; a search is already
   * the answer to "which one", so the matches come back as one flat list.
   */
  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return GROUPS
    const seen = new Set<string>()
    const matches = GROUPS.flatMap((g) => g.countries).filter((c) => {
      if (!c.toLowerCase().includes(q) || seen.has(c)) return false
      seen.add(c)
      return true
    })
    return [{ group: '', countries: matches }]
  }, [query])

  const pick = (country: string) => {
    onChange(country)
    close()
  }

  return (
    <div ref={wrapRef} className={`hero-source${open ? ' is-open' : ''}`}>
      {showLabel && (
        <span className="hero-source__label">
          {COST_BOM_DEMO_STRINGS.composerSourcing}
        </span>
      )}
      <div className="hero-source__control">
        <button
          type="button"
          className="hero-source__tag"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => (open ? close() : setOpen(true))}
        >
          {value}
          <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M4 6.5L8 10.5L12 6.5"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {open && (
          <div className="hero-source__pop" role="listbox">
            <input
              ref={searchRef}
              className="hero-source__search"
              value={query}
              placeholder={COST_BOM_DEMO_STRINGS.searchCountries}
              aria-label={COST_BOM_DEMO_STRINGS.searchCountries}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className="hero-source__list">
              {results.every((g) => g.countries.length === 0) && (
                <p className="hero-source__empty">
                  {COST_BOM_DEMO_STRINGS.noCountriesFound}
                </p>
              )}
              {results.map((g) => (
                <div key={g.group || 'matches'}>
                  {g.group && <p className="hero-source__group">{g.group}</p>}
                  {g.countries.map((country) => (
                    <button
                      key={country}
                      type="button"
                      role="option"
                      aria-selected={country === value}
                      className={`hero-source__opt${
                        country === value ? ' is-active' : ''
                      }`}
                      onClick={() => pick(country)}
                    >
                      {country}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
